import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { ButtonStyle } from '../../common/components/Button/Button';
import ButtonContainer from '../../common/components/Button/ButtonContainer';
import Container from '../../common/components/Container/Container';
import TitleContainer from '../../common/components/TitleContainer/TitleContainer';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import FeePanel from '../../common/components/FeePanel/FeePanel';
import Loading from '../../common/components/Loading/Loading';
import { Action } from '../../config';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';
import { useFees } from '../../providers/FeesContext';
import { useGKNService } from '../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';

function FundsCheckScreen(): JSX.Element {
  const { networkName, action, flowType, setFundsCheckPassed, navigateToNextFlow, chainName } =
    useContext(FlowConfigContext);
  const { showFeesAtStart, gknServiceData } = useGKNService();
  const { t } = useTranslation();
  const { walletBalanceDetail, walletCryptoBalance } = useFees();
  const labelPath = 'screens.fundsCheck';

  // auto-continue if the wallet has sufficient funds, is using (deprecated) casper, if the balance check failed
  // or if the showFeesAtStart flag is set in which case we want to show the fees screen regardless of the wallet balance
  useEffect(() => {
    // wait for gatekeeper network service data to be loaded
    if (!gknServiceData) {
      return;
    }
    const walletHasFunds =
      chainName === 'casper' || // we don't support casper in our balance check but haven't removed official support here so just proceed if the chain is casper
      !!walletBalanceDetail?.hasSufficientFunds || // proceed if the wallet has sufficient funds
      !!walletCryptoBalance?.error; // don't block the user if our balance check failed for some reason

    console.log('walletHasFunds', { walletHasFunds, walletBalanceDetail, walletCryptoBalance });
    setFundsCheckPassed(walletHasFunds);
    if (walletHasFunds && !showFeesAtStart) {
      navigateToNextFlow();
    }
  }, [
    navigateToNextFlow,
    setFundsCheckPassed,
    walletBalanceDetail,
    chainName,
    walletCryptoBalance?.error,
    walletCryptoBalance,
  ]);

  const renderFeePanel = () => (
    <React.Suspense
      fallback={
        <div className="my-8">
          <Loading />
        </div>
      }
    >
      <FeePanel fundsCheckMode />
    </React.Suspense>
  );

  const renderTitleContainer = () => {
    if (!showFeesAtStart && walletBalanceDetail?.hasSufficientFunds) {
      return null;
    }
    return (
      <TitleContainer center>
        <h1 data-testid={`${labelPath}.title`} className="mt-2">
          {t(`${labelPath}.title`, { context: networkName, flowType: t(`${flowType}`) })}
        </h1>
      </TitleContainer>
    );
  };

  const renderButtons = () => {
    if (!showFeesAtStart && walletBalanceDetail?.hasSufficientFunds) {
      return null;
    }
    return (
      <ButtonContainer>
        {walletBalanceDetail && !walletCryptoBalance?.error && !walletBalanceDetail?.hasSufficientFunds ? (
          <Button
            style={ButtonStyle.DEFAULT}
            disabled
            loading={!walletBalanceDetail}
            dataTestid="OK_BUTTON"
            onClick={() => {}}
          >
            <span>{t('common.fees.insufficientFunds')}</span>
          </Button>
        ) : (
          <>
            <Button
              style={ButtonStyle.DEFAULT}
              disabled={false}
              loading={!walletBalanceDetail && !walletCryptoBalance?.error}
              dataTestid="OK_BUTTON"
              onClick={() => navigateToNextFlow()}
            >
              <span>{t('common.fees.fundsContinue')}</span>
            </Button>
          </>
        )}
      </ButtonContainer>
    );
  };

  const renderContent = () => {
    switch (action) {
      case Action.ISSUANCE:
      case Action.REFRESH:
        return (
          <React.Fragment key="funds-check">
            {renderTitleContainer()}
            {renderFeePanel()}
            {renderButtons()}
          </React.Fragment>
        );
      default:
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    }
  };

  return <Container>{renderContent()}</Container>;
}

export default withRouteAnalytics(FundsCheckScreen, Screens.FUNDS_CHECK);
